import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import { useSettings } from '@/context/SettingsContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { format, parse, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarIcon, ArrowLeft, ShoppingBag, Minus, Plus, Trash2, AlertCircle } from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

// Detect mobile browser for optimized date picker
const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (window.innerWidth <= 768);
};

// Cross-browser compatible date picker component
const DatePicker = ({ value, onChange, minDate, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    setIsMobile(isMobileDevice());
    // Re-check on resize
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Format date for native input (yyyy-MM-dd)
  const nativeValue = value ? format(value, 'yyyy-MM-dd') : '';
  
  // Format date for display (dd.MM.yyyy)
  const displayValue = value ? format(value, 'dd.MM.yyyy', { locale: de }) : '';

  // Handle native date input change
  const handleNativeChange = useCallback((e) => {
    const dateStr = e.target.value;
    if (dateStr) {
      const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
      if (isValid(parsed)) {
        onChange(parsed);
      }
    } else {
      onChange(null);
    }
  }, [onChange]);

  // Handle calendar selection
  const handleCalendarSelect = useCallback((date) => {
    onChange(date);
    setIsOpen(false);
  }, [onChange]);

  // Get min date string for native input
  const minDateStr = minDate ? format(minDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  // Mobile: Use native date input for best compatibility
  if (isMobile) {
    return (
      <div className="relative">
        <div className="relative">
          <Input
            type="date"
            value={nativeValue}
            onChange={handleNativeChange}
            min={minDateStr}
            disabled={disabled}
            className="mt-1 rounded-sm pl-10 w-full"
            data-testid="input-arrival-date-native"
            style={{ 
              colorScheme: 'light',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              appearance: 'none'
            }}
          />
          <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        </div>
        {/* Hidden display of selected date for confirmation */}
        {value && (
          <p className="text-xs text-green-600 mt-1" data-testid="date-confirmation">
            ✓ Gewählt: {displayValue}
          </p>
        )}
      </div>
    );
  }

  // Desktop: Use Popover with Calendar for better UX
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          type="button"
          variant="outline" 
          className={`w-full mt-1 justify-start text-left font-normal rounded-sm ${!value ? 'text-slate-400' : ''}`} 
          data-testid="input-arrival-date"
          disabled={disabled}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? displayValue : 'Datum wählen'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start" sideOffset={4}>
        <Calendar 
          mode="single" 
          selected={value} 
          onSelect={handleCalendarSelect} 
          locale={de} 
          disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

export default function CheckoutPage() {
  const { items, totalAmount, updateQuantity, removeItem, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [arrivalDate, setArrivalDate] = useState(null);
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', booking_number: '', phone: '', message: '' });
  const { settings: s } = useSettings();
  const [minOrderValue, setMinOrderValue] = useState(20);
  const [serviceFee, setServiceFee] = useState(5);

  useEffect(() => {
    api.get('/shop-settings').then(r => {
      setMinOrderValue(r.data.min_order_value || 20);
      setServiceFee(r.data.service_fee || 5);
    }).catch(() => {});
  }, []);

  // Mindestbestellwert bezieht sich nur auf Produktsumme, NICHT auf Servicepauschale
  const isBelowMinimum = totalAmount < minOrderValue;
  const totalWithServiceFee = totalAmount + serviceFee;

  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleDateChange = useCallback((date) => {
    setArrivalDate(date);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) { 
      toast.error(s.msg_cart_empty || 'Warenkorb ist leer'); 
      return; 
    }
    
    if (isBelowMinimum) {
      toast.error(`Der Mindestbestellwert von ${minOrderValue.toFixed(2)} € wurde noch nicht erreicht.`);
      return;
    }
    
    if (!form.first_name || !form.last_name || !form.email || !form.booking_number) {
      toast.error(s.msg_fill_required || 'Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    if (!arrivalDate) {
      toast.error('Bitte wählen Sie ein Anreisedatum');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        ...form,
        arrival_date: format(arrivalDate, 'yyyy-MM-dd'),
        delivery_type: deliveryType,
        items: items.map(i => ({ 
          product_id: i.product_id, 
          name: i.name, 
          article_number: i.article_number, 
          quantity: i.quantity, 
          price: i.price, 
          total: i.total 
        })),
      };
      
      const res = await api.post('/orders', orderData);
      clearCart();
      toast.success(s.msg_order_success || 'Bestellung erfolgreich gesendet!');
      navigate(`/bestellung/${res.data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Senden der Bestellung');
    } finally { 
      setLoading(false); 
    }
  };

  if (items.length === 0) {
    return (
      <div className="pt-20 min-h-screen bg-[#F7F9FC]">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <ShoppingBag className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1E293B] mb-3">{s.checkout_empty_title || 'Warenkorb leer'}</h2>
          <p className="text-[#475569] mb-6">{s.checkout_empty_text || 'Fügen Sie Produkte hinzu'}</p>
          <Button onClick={() => navigate('/')} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide" data-testid="back-to-shop">
            <ArrowLeft className="w-4 h-4 mr-2" /> {s.checkout_back || 'Zurück zum Shop'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#F7F9FC]" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6 text-[#0E518D]" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> {s.checkout_back || 'Zurück'}
        </Button>
        <h1 className="text-3xl font-bold text-[#0E518D] mb-8">{s.checkout_title || 'Ihre Bestellung'}</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="bg-white rounded-sm shadow-sm border border-slate-100 p-6 lg:sticky lg:top-24" data-testid="order-summary">
              <h2 className="text-xl font-semibold text-[#1E293B] mb-4">{s.checkout_summary_title || 'Zusammenfassung'}</h2>
              <div className="space-y-3">
                {items.map(item => (
                  <div key={item.product_id} className="bg-slate-50 rounded-sm p-3" data-testid={`checkout-item-${item.product_id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0 mr-2">
                        <p className="font-medium text-[#1E293B] text-sm truncate">{item.name}</p>
                        <p className="text-xs text-slate-400">Art. {item.article_number}</p>
                      </div>
                      <button 
                        type="button"
                        onClick={() => removeItem(item.product_id)} 
                        className="text-slate-400 hover:text-red-500 flex-shrink-0 p-1"
                        aria-label="Entfernen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <button 
                          type="button"
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)} 
                          className="w-7 h-7 rounded-sm border border-slate-200 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200"
                          aria-label="Menge verringern"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                        <button 
                          type="button"
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)} 
                          className="w-7 h-7 rounded-sm border border-slate-200 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200"
                          aria-label="Menge erhöhen"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="font-bold text-[#0E518D] text-sm">{item.total.toFixed(2)} €</span>
                    </div>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              
              {/* Produktsumme */}
              <div className="flex justify-between items-center text-sm mb-2">
                <span className="text-slate-500">Produkte</span>
                <span className="font-medium">{totalAmount.toFixed(2)} €</span>
              </div>
              
              {/* Servicepauschale */}
              <div className="flex justify-between items-center text-sm mb-3">
                <span className="text-slate-500">Liefer- und Servicepauschale</span>
                <span className="font-medium">{serviceFee.toFixed(2)} €</span>
              </div>
              
              <Separator className="my-3" />
              
              {/* Gesamtsumme */}
              <div className="flex justify-between items-center">
                <span className="font-semibold text-[#1E293B]">Gesamtsumme</span>
                <span className="text-2xl font-bold text-[#0E518D]">{totalWithServiceFee.toFixed(2)} €</span>
              </div>
              
              {/* Mindestbestellwert Hinweis - bezieht sich auf Produktsumme */}
              <div className={`mt-4 p-3 rounded-sm ${isBelowMinimum ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="min-order-notice">
                {isBelowMinimum ? (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-700">Mindestbestellwert: {minOrderValue.toFixed(2)} €</p>
                      <p className="text-xs text-red-600 mt-0.5">Es fehlen noch {(minOrderValue - totalAmount).toFixed(2)} € (Produktsumme)</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-green-700">✓ Mindestbestellwert erreicht</p>
                )}
              </div>
              
              <p className="text-xs text-slate-400 mt-3 italic">{s.checkout_payment_hint || ''}</p>
            </div>
          </div>
          
          {/* Order Form */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <form onSubmit={handleSubmit} className="bg-white rounded-sm shadow-sm border border-slate-100 p-6" data-testid="order-form">
              <h2 className="text-xl font-semibold text-[#1E293B] mb-6">{s.checkout_form_title || 'Ihre Daten'}</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-sm font-medium text-[#1E293B]">Vorname *</Label>
                  <Input 
                    value={form.first_name} 
                    onChange={e => handleChange('first_name', e.target.value)} 
                    required 
                    className="mt-1 rounded-sm" 
                    data-testid="input-first-name"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1E293B]">Nachname *</Label>
                  <Input 
                    value={form.last_name} 
                    onChange={e => handleChange('last_name', e.target.value)} 
                    required 
                    className="mt-1 rounded-sm" 
                    data-testid="input-last-name"
                    autoComplete="family-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1E293B]">E-Mail *</Label>
                  <Input 
                    type="email" 
                    value={form.email} 
                    onChange={e => handleChange('email', e.target.value)} 
                    required 
                    className="mt-1 rounded-sm" 
                    data-testid="input-email"
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1E293B]">Buchungsnummer *</Label>
                  <Input 
                    value={form.booking_number} 
                    onChange={e => handleChange('booking_number', e.target.value)} 
                    required 
                    className="mt-1 rounded-sm" 
                    data-testid="input-booking-number"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1E293B]">Anreisedatum *</Label>
                  <DatePicker 
                    value={arrivalDate} 
                    onChange={handleDateChange}
                    minDate={new Date()}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#1E293B]">Telefon</Label>
                  <Input 
                    type="tel"
                    value={form.phone} 
                    onChange={e => handleChange('phone', e.target.value)} 
                    className="mt-1 rounded-sm" 
                    data-testid="input-phone"
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#1E293B]">Nachricht</Label>
                <Textarea 
                  value={form.message} 
                  onChange={e => handleChange('message', e.target.value)} 
                  rows={3} 
                  className="mt-1 rounded-sm" 
                  placeholder="Optionale Nachricht..." 
                  data-testid="input-message" 
                />
              </div>
              
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#1E293B] mb-3 block">Lieferart *</Label>
                <RadioGroup value={deliveryType} onValueChange={setDeliveryType} className="space-y-3">
                  <div className="flex items-center space-x-3 bg-slate-50 p-4 rounded-sm border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <RadioGroupItem value="pickup" id="pickup" data-testid="delivery-pickup" />
                    <Label htmlFor="pickup" className="cursor-pointer text-sm text-[#1E293B] flex-1">{s.delivery_pickup || 'Abholung im Büro'}</Label>
                  </div>
                  <div className="flex items-center space-x-3 bg-slate-50 p-4 rounded-sm border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors">
                    <RadioGroupItem value="delivery" id="delivery" data-testid="delivery-apartment" />
                    <Label htmlFor="delivery" className="cursor-pointer text-sm text-[#1E293B] flex-1">{s.delivery_apartment || 'Lieferung in die Ferienwohnung'}</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <Button 
                type="submit" 
                disabled={loading || isBelowMinimum} 
                className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide font-medium py-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed" 
                data-testid="submit-order"
              >
                {loading ? 'Wird gesendet...' : (s.checkout_submit || 'Bestellung absenden')}
              </Button>
              
              {isBelowMinimum && (
                <p className="text-xs text-red-500 text-center mt-2">
                  Bitte erreichen Sie den Mindestbestellwert von {minOrderValue.toFixed(2)} €
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
